import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { localDateStr } from '../utils/date'
import { koreanAmount } from '../utils/number'
import { COMPANY } from '../constants/company'
import { fetchClients, upsertClient } from '../utils/clients'
import useIsMobile from '../hooks/useIsMobile'

const INK = '#1a1916'
const MIN_ROWS = 22 // 인쇄 시 빈 줄 포함 최소 행 수

const emptyItem = () => ({ name:'', spec:'', qty:'', unit_price:'', supply:'', note:'' })

const num = v => {
  const n = Number(String(v ?? '').replace(/,/g, ''))
  return Number.isFinite(n) ? n : 0
}
const fmt = v => (num(v) === 0 ? '' : num(v).toLocaleString('ko-KR'))

/**
 * 품목 1줄의 공급가액/세액 (거래명세표와 동일 규칙)
 * - 공급가액 직접 입력 시 그 값 사용
 * - 아니면 수량×단가, 수량 미입력 시 1로 간주
 */
function calcItem(it) {
  const manual = String(it.supply ?? '').trim()
  let supply
  if (manual !== '') {
    supply = num(manual)
  } else {
    const price = num(it.unit_price)
    const qtyRaw = String(it.qty ?? '').trim()
    const qty = qtyRaw === '' ? (price > 0 ? 1 : 0) : num(it.qty)
    supply = qty * price
  }
  const tax = Math.round(supply * 0.1)
  return { supply, tax }
}

function calcTotals(items) {
  let supplyTotal = 0, taxTotal = 0, qtyTotal = 0
  for (const it of items) {
    const { supply, tax } = calcItem(it)
    supplyTotal += supply
    taxTotal += tax
    qtyTotal += num(it.qty)
  }
  return { supplyTotal, taxTotal, qtyTotal, grandTotal: supplyTotal + taxTotal }
}

function fmtKoreanDate(dateStr) {
  if (!dateStr) return ''
  const [y, m, d] = dateStr.split('-')
  return `${y}년 ${Number(m)}월 ${Number(d)}일`
}

/* ——— 흑백 견적서 양식 공용 스타일 ——— */
const bd = `1px solid ${INK}`
const bdBold = `2px solid ${INK}`
const thS = { border:bd, padding:'0 1mm', fontSize:'8.5pt', fontWeight:700, color:INK, textAlign:'center', whiteSpace:'nowrap', background:'#f4f3f0', lineHeight:1.2 }
const tdS = { border:bd, padding:'0 1.5mm', fontSize:'9pt', color:INK, lineHeight:1.2 }
const lblS = { ...thS, width:'20mm', fontSize:'7.5pt' }
const cellInput = {
  width:'100%', border:'none', outline:'none', background:'transparent',
  fontFamily:'inherit', fontSize:'inherit', fontWeight:'inherit', color:'inherit',
  textAlign:'inherit', padding:0, boxSizing:'border-box',
}

export default function Quote({ profile }) {
  const [list, setList] = useState([])
  const [showModal, setShowModal] = useState(false)
  const [showDetail, setShowDetail] = useState(null)
  const [search, setSearch] = useState('')
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(null)
  const [loading, setLoading] = useState(false)
  const [clients, setClients] = useState([])
  const isMobile = useIsMobile()

  useEffect(() => { fetchList(); fetchClients().then(setClients) }, [])

  async function fetchList() {
    const { data, error } = await supabase.from('quotes').select('*').order('created_at', { ascending: false })
    if (error) console.error('quotes 조회:', error.message)
    if (data) setList(data)
  }

  const padItems = arr => {
    const items = arr.map(it => ({ ...it }))
    while (items.length < MIN_ROWS) items.push(emptyItem())
    return items
  }

  function openNewModal() {
    setEditingId(null)
    setForm({
      quote_no: '',
      quote_date: localDateStr(),
      customer_name: '',
      biz_type: COMPANY.bizType,
      biz_item: COMPANY.bizItem,
      items: padItems([]),
    })
    setShowModal(true)
  }

  function openEditModal(qt) {
    setEditingId(qt.id)
    setForm({
      quote_no: qt.quote_no || '',
      quote_date: qt.quote_date,
      customer_name: qt.customer_name || '',
      biz_type: qt.biz_type || COMPANY.bizType,
      biz_item: qt.biz_item || COMPANY.bizItem,
      items: padItems(Array.isArray(qt.items) ? qt.items : []),
    })
    setShowDetail(null)
    setShowModal(true)
  }

  function setItem(idx, patch) {
    setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }))
  }

  function addItemRow() {
    setForm(f => ({ ...f, items: [...f.items, emptyItem()] }))
  }

  function clearItemRow(idx) {
    setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? emptyItem() : it) }))
  }

  async function submitQuote() {
    if (!form.customer_name) { alert('받는 곳(귀하)을 입력하세요'); return }
    const validItems = form.items.filter(it => it.name || num(it.qty) || num(it.unit_price) || num(it.supply))
    if (!validItems.length) { alert('품명을 1개 이상 입력하세요'); return }
    setLoading(true)
    const { supplyTotal, taxTotal, grandTotal } = calcTotals(validItems)
    const payload = {
      quote_no: form.quote_no,
      quote_date: form.quote_date || localDateStr(),
      customer_name: form.customer_name,
      biz_type: form.biz_type,
      biz_item: form.biz_item,
      items: validItems,
      supply_total: supplyTotal,
      tax_total: taxTotal,
      grand_total: grandTotal,
      created_by: profile.id,
      created_by_name: profile.name,
    }
    const q = editingId
      ? supabase.from('quotes').update(payload).eq('id', editingId)
      : supabase.from('quotes').insert(payload)
    const { error } = await q
    if (error) {
      alert('저장 실패: ' + error.message + '\n\nSupabase에 quotes 테이블이 있는지 확인하세요 (supabase/sql/quotes.sql 실행)')
      setLoading(false)
      return
    }
    // 새 거래처면 자동 등록 (실패해도 무시)
    await upsertClient({ name: form.customer_name })
    fetchClients().then(setClients)
    setShowModal(false)
    fetchList()
    setLoading(false)
  }

  /** 견적서 → 거래명세표 변환 */
  async function convertToInvoice(qt) {
    if (!window.confirm(`이 견적서를 거래명세표로 변환할까요?\n품목·금액·거래처가 그대로 복사됩니다.`)) return
    const today = localDateStr()
    const [, mm, dd] = today.split('-')
    const items = (Array.isArray(qt.items) ? qt.items : []).map(it => ({
      month: String(Number(mm)),
      day: String(Number(dd)),
      name: it.name || '',
      spec: it.spec || '',
      qty: it.qty ?? '',
      unit_price: it.unit_price ?? '',
      supply: it.supply ?? '',
      note: it.note || '',
    }))
    // 거래처 정보가 등록돼 있으면 주소·연락처 보강
    const cl = clients.find(c => c.name === qt.customer_name)
    const { error } = await supabase.from('invoices').insert({
      trade_date: today,
      buyer_name: qt.customer_name,
      buyer_addr: cl?.addr || '',
      buyer_tel: cl?.tel || '',
      buyer_fax: cl?.fax || '',
      receiver: '',
      misu: 0,
      items,
      supply_total: num(qt.supply_total),
      tax_total: num(qt.tax_total),
      grand_total: num(qt.grand_total),
      created_by: profile.id,
      created_by_name: profile.name,
    })
    if (error) {
      alert('변환 실패: ' + error.message + '\n\ninvoices 테이블이 있는지 확인하세요')
      return
    }
    alert('거래명세표로 변환 완료!\n거래명세표 탭에서 확인·수정·인쇄할 수 있습니다.')
  }

  async function deleteQuote(id) {
    if (!window.confirm('이 견적서를 삭제할까요?')) return
    const { error } = await supabase.from('quotes').delete().eq('id', id)
    if (error) { alert('삭제 실패: ' + error.message); return }
    setShowDetail(null)
    fetchList()
  }

  const filtered = list.filter(qt => {
    const q = search.toLowerCase()
    if (!q) return true
    return qt.customer_name?.toLowerCase().includes(q) || qt.quote_no?.toLowerCase().includes(q) || qt.quote_date?.includes(q)
  })

  const inputStyle = { width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', boxSizing:'border-box' }
  const formTotals = form ? calcTotals(form.items) : null

  return (
    <div>
      {/* 헤더 */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:10 }}>
        <div>
          <div style={{ fontSize:18, fontWeight:700, marginBottom:4 }}>견적서</div>
          <div style={{ fontSize:13, color:'#a09e99' }}>작성 · 저장 · 인쇄 (공급자: {COMPANY.name})</div>
        </div>
        <button onClick={openNewModal}
          style={{ padding:'8px 16px', borderRadius:8, border:'none', background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>
          + 견적서 작성
        </button>
      </div>

      {/* 검색 */}
      <div style={{ marginBottom:20 }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="업체명, 번호, 날짜 검색..."
          style={inputStyle} />
      </div>

      {/* 모바일: 카드 / 데스크톱: 테이블 */}
      {isMobile ? (
        filtered.length === 0 ? (
          <div style={{ background:'#fff', border:'1px solid #e5e3de', borderRadius:10, textAlign:'center', padding:40, color:'#a09e99', fontSize:13 }}>
            작성된 견적서가 없습니다
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {filtered.map(qt => (
              <div key={qt.id} onClick={() => setShowDetail(qt)}
                style={{ background:'#fff', border:'1px solid #e5e3de', borderRadius:10, padding:14, cursor:'pointer', boxShadow:'0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6, gap:8 }}>
                  <span style={{ fontSize:14, fontWeight:700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{qt.customer_name} 귀하</span>
                  <span style={{ fontSize:11, color:'#a09e99', flexShrink:0 }}>{qt.quote_date}</span>
                </div>
                <div style={{ fontSize:12, color:'#6b6860', marginBottom:6 }}>
                  {qt.items?.[0]?.name}{qt.items?.length > 1 ? ` 외 ${qt.items.length - 1}건` : ''}
                </div>
                <div style={{ textAlign:'right', fontSize:15, fontWeight:700, color:'#2d5a3d' }}>₩ {num(qt.grand_total).toLocaleString()}</div>
              </div>
            ))}
          </div>
        )
      ) : (
      <div style={{ background:'#fff', border:'1px solid #e5e3de', borderRadius:10, overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr>
              {['일자','No.','받는 곳','품명','합계금액','작성자',''].map((h, i) => (
                <th key={i} style={{ padding:'10px 14px', fontSize:11, fontWeight:600, color:'#a09e99', borderBottom:'1px solid #e5e3de', background:'#f9f8f6', textAlign:'left', whiteSpace:'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign:'center', padding:40, color:'#a09e99' }}>작성된 견적서가 없습니다</td></tr>
            ) : filtered.map(qt => (
              <tr key={qt.id} style={{ borderBottom:'1px solid #e5e3de' }}>
                <td style={{ padding:'12px 14px', whiteSpace:'nowrap' }}>{qt.quote_date}</td>
                <td style={{ padding:'12px 14px', fontSize:12, color:'#6b6860' }}>{qt.quote_no || '—'}</td>
                <td style={{ padding:'12px 14px', fontWeight:600 }}>{qt.customer_name}</td>
                <td style={{ padding:'12px 14px', fontSize:12, color:'#6b6860', maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {qt.items?.[0]?.name}{qt.items?.length > 1 ? ` 외 ${qt.items.length - 1}건` : ''}
                </td>
                <td style={{ padding:'12px 14px', textAlign:'right', fontWeight:700, whiteSpace:'nowrap' }}>{num(qt.grand_total).toLocaleString()}</td>
                <td style={{ padding:'12px 14px', fontSize:12, color:'#6b6860' }}>{qt.created_by_name}</td>
                <td style={{ padding:'12px 14px' }}>
                  <button onClick={() => setShowDetail(qt)}
                    style={{ padding:'4px 12px', border:'1px solid #e5e3de', borderRadius:6, background:'#fff', cursor:'pointer', fontSize:12, whiteSpace:'nowrap' }}>
                    보기/인쇄
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {/* 작성/수정 모달 — 양식 그대로 편집 */}
      {showModal && form && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div style={{ background:'#fff', borderRadius:14, width:820, maxWidth:'96vw', maxHeight:'92vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #e5e3de', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:15, fontWeight:700 }}>{editingId ? '견적서 수정' : '견적서 작성'}</span>
              <button onClick={() => setShowModal(false)} style={{ border:'none', background:'#f9f8f6', borderRadius:6, width:28, height:28, cursor:'pointer', fontSize:16 }}>✕</button>
            </div>
            <div style={{ padding:16, overflowX:'auto' }}>
              {/* 거래처 불러오기 */}
              {clients.length > 0 && (
                <div style={{ maxWidth:'197mm', margin:'0 auto 10px' }}>
                  <select value="" onChange={e => {
                    const c = clients.find(x => x.id === e.target.value)
                    if (c) setForm(f => ({ ...f, customer_name: c.name }))
                  }}
                    style={{ padding:'8px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', cursor:'pointer', maxWidth:'100%' }}>
                    <option value="">📇 거래처 불러오기 (선택 시 자동 입력)</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
              <div style={{ width:'197mm', margin:'0 auto', fontFamily:'Noto Sans KR, sans-serif', background:'#fff', border:bdBold, padding:'4mm' }}>
                {/* No. + 제목 */}
                <div style={{ marginBottom:'2mm' }}>
                  <div style={{ fontSize:'9pt', fontStyle:'italic', display:'flex', alignItems:'baseline', gap:'1mm' }}>
                    No.
                    <input value={form.quote_no} onChange={e => setForm({...form, quote_no:e.target.value})}
                      style={{ ...cellInput, borderBottom:bd, fontSize:'9pt', width:'28mm', textAlign:'center' }} />
                  </div>
                  <div style={{ textAlign:'center', fontSize:'18pt', fontWeight:800, letterSpacing:'0.5em', textDecoration:'underline', textUnderlineOffset:'2mm', marginTop:'1mm' }}>
                    견 적 서
                  </div>
                </div>

                {/* 좌: 날짜·귀하 / 우: 공급자 */}
                <div style={{ display:'flex', gap:'3mm', marginTop:'3mm' }}>
                  <div style={{ flex:1, fontSize:'9.5pt', paddingTop:'2mm' }}>
                    <div style={{ marginBottom:'3mm' }}>
                      {fmtKoreanDate(form.quote_date)}
                      <input type="date" value={form.quote_date} onChange={e => setForm({...form, quote_date:e.target.value})}
                        style={{ display:'block', border:'none', outline:'none', background:'#f9f8f6', fontSize:'8pt', fontFamily:'inherit', marginTop:'1mm' }} />
                    </div>
                    <div style={{ display:'flex', alignItems:'baseline', gap:'2mm', marginBottom:'4mm' }}>
                      <input value={form.customer_name} onChange={e => setForm({...form, customer_name:e.target.value})}
                        placeholder="받는 곳 *"
                        style={{ ...cellInput, borderBottom:bd, width:'52mm', textAlign:'center', fontSize:'11pt', fontWeight:700 }} />
                      <b style={{ fontSize:'10.5pt' }}>귀하</b>
                    </div>
                    <div>아래와 같이 견적합니다.</div>
                  </div>
                  <table style={{ borderCollapse:'collapse', width:'112mm', border:bd, tableLayout:'fixed', flexShrink:0 }}>
                    <tbody>
                      <tr style={{ height:'7mm' }}>
                        <td rowSpan={5} style={{ ...tdS, width:'6mm', textAlign:'center', fontWeight:700, fontSize:'8pt', padding:'0 0.5mm' }}>
                          공<br/><br/>급<br/><br/>자
                        </td>
                        <td style={{ ...lblS, width:'18mm' }}>등 록 번 호</td>
                        <td colSpan={3} style={{ ...tdS, fontWeight:700, letterSpacing:'0.1em' }}>{COMPANY.regNo}</td>
                      </tr>
                      <tr style={{ height:'7mm' }}>
                        <td style={lblS}>상호(법인명)</td>
                        <td style={{ ...tdS, textAlign:'center', fontWeight:700 }}>{COMPANY.name}</td>
                        <td style={{ ...lblS, width:'10mm' }}>성명</td>
                        <td style={{ ...tdS, width:'22mm', textAlign:'center', fontWeight:600, fontSize:'8.5pt' }}>{COMPANY.ceo} <span style={{ fontSize:'7pt', color:'#6b6860' }}>(인)</span></td>
                      </tr>
                      <tr style={{ height:'7mm' }}>
                        <td style={lblS}>사업장주소</td>
                        <td colSpan={3} style={{ ...tdS, fontSize:'8pt' }}>{COMPANY.addr}</td>
                      </tr>
                      <tr style={{ height:'7mm' }}>
                        <td style={lblS}>업 태</td>
                        <td style={{ ...tdS, textAlign:'center' }}>
                          <input value={form.biz_type} onChange={e => setForm({...form, biz_type:e.target.value})} style={cellInput} />
                        </td>
                        <td style={{ ...lblS, width:'10mm' }}>종목</td>
                        <td style={{ ...tdS, textAlign:'center' }}>
                          <input value={form.biz_item} onChange={e => setForm({...form, biz_item:e.target.value})} style={cellInput} />
                        </td>
                      </tr>
                      <tr style={{ height:'7mm' }}>
                        <td style={lblS}>전 화 번 호</td>
                        <td colSpan={3} style={{ ...tdS, fontSize:'8.5pt' }}>{COMPANY.tel} / Fax. {COMPANY.fax}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* 합계금액 */}
                <table style={{ borderCollapse:'collapse', width:'100%', border:bdBold, marginTop:'3mm', tableLayout:'fixed' }}>
                  <tbody>
                    <tr style={{ height:'10mm' }}>
                      <td style={{ ...tdS, width:'40mm', borderRight:bd }}>
                        <div style={{ fontSize:'11pt', fontWeight:800, letterSpacing:'0.2em' }}>합 계 금 액</div>
                        <div style={{ fontSize:'7pt', color:'#6b6860' }}>(공급가액+세액)</div>
                      </td>
                      <td style={{ ...tdS, textAlign:'center', fontSize:'12pt', fontWeight:700 }}>
                        {koreanAmount(formTotals.grandTotal)}원 (₩ {formTotals.grandTotal.toLocaleString()} )
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* 품목 테이블 */}
                <table style={{ borderCollapse:'collapse', width:'100%', border:bdBold, borderTop:'none', tableLayout:'fixed' }}>
                  <thead>
                    <tr style={{ height:'7mm' }}>
                      <th style={thS}>품 명</th>
                      <th style={{ ...thS, width:'22mm' }}>규 격</th>
                      <th style={{ ...thS, width:'14mm' }}>수 량</th>
                      <th style={{ ...thS, width:'22mm' }}>단 가</th>
                      <th style={{ ...thS, width:'26mm' }}>공 급 가 액</th>
                      <th style={{ ...thS, width:'22mm' }}>세 액</th>
                      <th style={{ ...thS, width:'18mm' }}>비 고</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((it, i) => {
                      const { supply, tax } = calcItem(it)
                      const manualSupply = String(it.supply ?? '').trim()
                      const hasContent = it.name || it.spec || it.note || String(it.qty).trim() || String(it.unit_price).trim() || manualSupply
                      const cellTd = { ...tdS, fontSize:'8.5pt' }
                      return (
                        <tr key={i} style={{ height:'6.5mm' }}>
                          <td style={cellTd}><input value={it.name} onChange={e => setItem(i, { name:e.target.value })} style={cellInput} /></td>
                          <td style={{ ...cellTd, textAlign:'center' }}><input value={it.spec} onChange={e => setItem(i, { spec:e.target.value })} style={cellInput} /></td>
                          <td style={{ ...cellTd, textAlign:'right' }}><input value={it.qty} onChange={e => setItem(i, { qty:e.target.value, supply:'' })} style={cellInput} /></td>
                          <td style={{ ...cellTd, textAlign:'right' }}><input value={it.unit_price} onChange={e => setItem(i, { unit_price:e.target.value, supply:'' })} style={cellInput} /></td>
                          <td style={{ ...cellTd, textAlign:'right' }}>
                            <input value={manualSupply !== '' ? it.supply : (supply ? supply.toLocaleString() : '')}
                              onChange={e => setItem(i, { supply:e.target.value })} style={cellInput} />
                          </td>
                          <td style={{ ...cellTd, textAlign:'right', background:'#fafaf8', color:'#6b6860' }}>{tax ? tax.toLocaleString() : ''}</td>
                          <td style={{ ...cellTd, position:'relative' }}>
                            <input value={it.note} onChange={e => setItem(i, { note:e.target.value })} style={cellInput} />
                            {hasContent && (
                              <button type="button" onClick={() => clearItemRow(i)} title="줄 비우기"
                                style={{ position:'absolute', right:1, top:'50%', transform:'translateY(-50%)', border:'none', background:'transparent', color:'#b83030', cursor:'pointer', fontSize:10, padding:'0 2px', lineHeight:1 }}>✕</button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                    {/* 계 */}
                    <tr style={{ height:'8mm', background:'#f4f3f0' }}>
                      <td style={{ ...tdS, textAlign:'center', fontWeight:700 }}>계</td>
                      <td style={tdS} />
                      <td style={{ ...tdS, textAlign:'right', fontWeight:700 }}>{formTotals.qtyTotal || ''}</td>
                      <td style={tdS} />
                      <td style={{ ...tdS, textAlign:'right', fontWeight:700 }}>{formTotals.supplyTotal.toLocaleString()}</td>
                      <td style={{ ...tdS, textAlign:'right', fontWeight:700 }}>{formTotals.taxTotal.toLocaleString()}</td>
                      <td style={tdS} />
                    </tr>
                  </tbody>
                </table>
              </div>

              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', maxWidth:'197mm', margin:'10px auto 0' }}>
                <span style={{ fontSize:11, color:'#a09e99' }}>
                  칸을 클릭해서 바로 입력 · 수량·단가 없이 공급가액만 입력해도 됨 · 세액(10%)·합계 자동
                </span>
                <button type="button" onClick={addItemRow}
                  style={{ padding:'4px 12px', border:'1px solid #e5e3de', borderRadius:6, background:'#fff', cursor:'pointer', fontSize:12, flexShrink:0 }}>
                  + 줄 추가
                </button>
              </div>
            </div>
            <div style={{ padding:'16px 24px', borderTop:'1px solid #e5e3de', display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button onClick={() => setShowModal(false)}
                style={{ padding:'8px 16px', border:'1px solid #e5e3de', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:13 }}>취소</button>
              <button onClick={submitQuote} disabled={loading}
                style={{ padding:'8px 16px', border:'none', borderRadius:8, background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>
                {loading ? '저장 중...' : (editingId ? '수정하기' : '저장하기')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 보기/인쇄 모달 */}
      {showDetail && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={e => e.target === e.currentTarget && setShowDetail(null)}>
          <style>{`
            @page { size: A4 portrait; margin: 8mm; }
            @media print {
              body * { visibility: hidden !important; }
              .quote-print, .quote-print * { visibility: visible !important; }
              .quote-print { position: fixed !important; left: 0; top: 0; margin: 0 !important; }
            }
          `}</style>
          <div style={{ background:'#fff', borderRadius:14, width:880, maxWidth:'96vw', maxHeight:'92vh', overflow:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding:'14px 20px', borderBottom:'1px solid #e5e3de', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:14, fontWeight:700 }}>{showDetail.quote_date} · {showDetail.customer_name} 귀하</span>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                <button onClick={() => convertToInvoice(showDetail)}
                  style={{ padding:'6px 14px', border:'none', borderRadius:8, background:'#e8eef8', color:'#1e4a8c', cursor:'pointer', fontSize:12, fontWeight:600 }}>🧾 거래명세표 변환</button>
                <button onClick={() => openEditModal(showDetail)}
                  style={{ padding:'6px 14px', border:'1px solid #e5e3de', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:12 }}>✏️ 수정</button>
                <button onClick={() => deleteQuote(showDetail.id)}
                  style={{ padding:'6px 14px', border:'none', borderRadius:8, background:'#fdeaea', color:'#b83030', cursor:'pointer', fontSize:12 }}>🗑️ 삭제</button>
                <button onClick={() => window.print()}
                  style={{ padding:'6px 14px', border:'none', borderRadius:8, background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:12, fontWeight:600 }}>🖨️ 인쇄</button>
                <button onClick={() => setShowDetail(null)}
                  style={{ border:'none', background:'#f9f8f6', borderRadius:6, width:28, height:28, cursor:'pointer', fontSize:16 }}>✕</button>
              </div>
            </div>
            <div style={{ padding:16, overflowX:'auto' }}>
              <QuotePrintSheet qt={showDetail} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/** 견적서 인쇄 양식 — A4 세로 1장 */
function QuotePrintSheet({ qt }) {
  const items = Array.isArray(qt.items) ? qt.items : []
  const rows = [...items]
  while (rows.length < MIN_ROWS) rows.push(null)
  const totals = calcTotals(items)

  return (
    <div className="quote-print" style={{ width:'197mm', margin:'0 auto', fontFamily:'Noto Sans KR, sans-serif', background:'#fff', border:bdBold, padding:'4mm', boxSizing:'border-box' }}>
      {/* No. + 제목 */}
      <div style={{ marginBottom:'2mm' }}>
        <div style={{ fontSize:'9pt', fontStyle:'italic' }}>
          No. <span style={{ borderBottom:bd, padding:'0 3mm', display:'inline-block', minWidth:'24mm', textAlign:'center' }}>{qt.quote_no}</span>
        </div>
        <div style={{ textAlign:'center', fontSize:'18pt', fontWeight:800, letterSpacing:'0.5em', textDecoration:'underline', textUnderlineOffset:'2mm', marginTop:'1mm' }}>
          견 적 서
        </div>
      </div>

      {/* 좌: 날짜·귀하 / 우: 공급자 */}
      <div style={{ display:'flex', gap:'3mm', marginTop:'3mm' }}>
        <div style={{ flex:1, fontSize:'9.5pt', paddingTop:'2mm' }}>
          <div style={{ marginBottom:'4mm' }}>{fmtKoreanDate(qt.quote_date)}</div>
          <div style={{ display:'flex', alignItems:'baseline', gap:'2mm', marginBottom:'5mm' }}>
            <span style={{ borderBottom:bd, minWidth:'52mm', display:'inline-block', textAlign:'center', fontSize:'11pt', fontWeight:700, padding:'0 2mm' }}>
              {qt.customer_name}
            </span>
            <b style={{ fontSize:'10.5pt' }}>귀하</b>
          </div>
          <div>아래와 같이 견적합니다.</div>
        </div>
        <table style={{ borderCollapse:'collapse', width:'112mm', border:bd, tableLayout:'fixed', flexShrink:0 }}>
          <tbody>
            <tr style={{ height:'7mm' }}>
              <td rowSpan={5} style={{ ...tdS, width:'6mm', textAlign:'center', fontWeight:700, fontSize:'8pt', padding:'0 0.5mm' }}>
                공<br/><br/>급<br/><br/>자
              </td>
              <td style={{ ...lblS, width:'18mm' }}>등 록 번 호</td>
              <td colSpan={3} style={{ ...tdS, fontWeight:700, letterSpacing:'0.1em' }}>{COMPANY.regNo}</td>
            </tr>
            <tr style={{ height:'7mm' }}>
              <td style={lblS}>상호(법인명)</td>
              <td style={{ ...tdS, textAlign:'center', fontWeight:700 }}>{COMPANY.name}</td>
              <td style={{ ...lblS, width:'10mm' }}>성명</td>
              <td style={{ ...tdS, width:'22mm', textAlign:'center', fontWeight:600, fontSize:'8.5pt' }}>{COMPANY.ceo} <span style={{ fontSize:'7pt', color:'#6b6860' }}>(인)</span></td>
            </tr>
            <tr style={{ height:'7mm' }}>
              <td style={lblS}>사업장주소</td>
              <td colSpan={3} style={{ ...tdS, fontSize:'8pt' }}>{COMPANY.addr}</td>
            </tr>
            <tr style={{ height:'7mm' }}>
              <td style={lblS}>업 태</td>
              <td style={{ ...tdS, textAlign:'center' }}>{qt.biz_type}</td>
              <td style={{ ...lblS, width:'10mm' }}>종목</td>
              <td style={{ ...tdS, textAlign:'center' }}>{qt.biz_item}</td>
            </tr>
            <tr style={{ height:'7mm' }}>
              <td style={lblS}>전 화 번 호</td>
              <td colSpan={3} style={{ ...tdS, fontSize:'8.5pt' }}>{COMPANY.tel} / Fax. {COMPANY.fax}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* 합계금액 */}
      <table style={{ borderCollapse:'collapse', width:'100%', border:bdBold, marginTop:'3mm', tableLayout:'fixed' }}>
        <tbody>
          <tr style={{ height:'10mm' }}>
            <td style={{ ...tdS, width:'40mm', borderRight:bd }}>
              <div style={{ fontSize:'11pt', fontWeight:800, letterSpacing:'0.2em' }}>합 계 금 액</div>
              <div style={{ fontSize:'7pt', color:'#6b6860' }}>(공급가액+세액)</div>
            </td>
            <td style={{ ...tdS, textAlign:'center', fontSize:'12pt', fontWeight:700 }}>
              {koreanAmount(qt.grand_total)}원 (₩ {num(qt.grand_total).toLocaleString()} )
            </td>
          </tr>
        </tbody>
      </table>

      {/* 품목 테이블 */}
      <table style={{ borderCollapse:'collapse', width:'100%', border:bdBold, borderTop:'none', tableLayout:'fixed' }}>
        <thead>
          <tr style={{ height:'7mm' }}>
            <th style={thS}>품 명</th>
            <th style={{ ...thS, width:'22mm' }}>규 격</th>
            <th style={{ ...thS, width:'14mm' }}>수 량</th>
            <th style={{ ...thS, width:'22mm' }}>단 가</th>
            <th style={{ ...thS, width:'26mm' }}>공 급 가 액</th>
            <th style={{ ...thS, width:'22mm' }}>세 액</th>
            <th style={{ ...thS, width:'18mm' }}>비 고</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((it, i) => {
            const c = it ? calcItem(it) : null
            return (
              <tr key={i} style={{ height:'6.5mm' }}>
                <td style={{ ...tdS, fontSize:'8.5pt', overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis' }}>{it?.name}</td>
                <td style={{ ...tdS, textAlign:'center', fontSize:'8.5pt' }}>{it?.spec}</td>
                <td style={{ ...tdS, textAlign:'right', fontSize:'8.5pt' }}>{it ? fmt(it.qty) : ''}</td>
                <td style={{ ...tdS, textAlign:'right', fontSize:'8.5pt' }}>{it ? fmt(it.unit_price) : ''}</td>
                <td style={{ ...tdS, textAlign:'right', fontSize:'8.5pt' }}>{c ? fmt(c.supply) : ''}</td>
                <td style={{ ...tdS, textAlign:'right', fontSize:'8.5pt' }}>{c ? fmt(c.tax) : ''}</td>
                <td style={{ ...tdS, fontSize:'8.5pt' }}>{it?.note}</td>
              </tr>
            )
          })}
          {/* 계 */}
          <tr style={{ height:'8mm', background:'#f4f3f0' }}>
            <td style={{ ...tdS, textAlign:'center', fontWeight:700 }}>계</td>
            <td style={tdS} />
            <td style={{ ...tdS, textAlign:'right', fontWeight:700 }}>{totals.qtyTotal || ''}</td>
            <td style={tdS} />
            <td style={{ ...tdS, textAlign:'right', fontWeight:700 }}>{totals.supplyTotal.toLocaleString()}</td>
            <td style={{ ...tdS, textAlign:'right', fontWeight:700 }}>{totals.taxTotal.toLocaleString()}</td>
            <td style={tdS} />
          </tr>
        </tbody>
      </table>
    </div>
  )
}
